import React, { useCallback, useContext, useEffect, useMemo } from "react";

import logger from "../../lib/logger";
import { getApiUrl } from "../../lib/common/getApiUrl";

const CLIENT_TOKEN =
  process.env.NODE_ENV === "development"
    ? process.env.API_CLIENT_TOKEN
    : window.location.pathname.split("/")[2];

interface FileUploadContextValue {
  socket: WebSocket;
  onUpload: (fileUpload: FileUpload) => Promise<Array<UploadedFile>>;
}

const FileUploadContext = React.createContext<FileUploadContextValue>(null!);

export const useFileUploadContext = () => useContext(FileUploadContext);

interface FileUploadProviderProps {
  children: React.ReactNode;
}

export const FileUploadProvider = React.memo<FileUploadProviderProps>(
  (props) => {
    const { children } = props;

    const apiUrl = getApiUrl();

    const baseURL = apiUrl.replace(/(http)(s)?\:\/\//, "ws$2://");

    const socketURL = useMemo(() => new URL(`${baseURL}/upload`), [baseURL]);

    if (CLIENT_TOKEN) {
      socketURL.searchParams.set("clientToken", CLIENT_TOKEN);
    }

    const socket = useMemo(() => new WebSocket(socketURL), [socketURL]);

    useEffect(() => {
      socket.onopen = () =>
        logger.debug("[File upload socket] Connection opened");

      socket.onerror = (error) =>
        logger.error("[File upload socket] Connection error", error);

      socket.onclose = (event) =>
        logger.debug("[File upload socket] Disconnected", event.reason);

      return () => {
        if (socket) {
          socket.close();
        }
      };
    }, [socket]);

    const onUpload = useCallback(
      (fileUpload: FileUpload): Promise<Array<UploadedFile>> => {
        return new Promise((resolve, reject) => {
          if (!socket) {
            throw new Error("Socket not ready");
          }

          socket.send(
            JSON.stringify({
              filename: fileUpload.file.name,
              file: fileUpload.data,
            }),
          );

          socket.onerror = reject;
          socket.onmessage = (e) => {
            let message:
              | undefined
              | {
                  error: boolean;
                  file: {
                    filename: string;
                    id: string;
                    md5: string;
                    path: string;
                  };
                  message: string;
                };

            try {
              message = JSON.parse(e.data);
            } catch {
              message = e.data;
            }

            if (!message) {
              return reject(new Error("Failed to upload the file"));
            }

            return resolve([message]);
          };
        });
      },
      [socket],
    );

    const value = useMemo(
      () => ({
        socket,
        onUpload,
      }),
      [],
    );

    return (
      <FileUploadContext.Provider value={value}>
        {children}
      </FileUploadContext.Provider>
    );
  },
);

export default FileUploadProvider;
